/**
 * Keeps the Quick Add panel's media, price and Add to Cart target in sync
 * with the selected color/variant swatch.
 *
 * This reads its own embedded `<script data-quick-add-variants>` data blob
 * (see snippets/quick-add-panel.liquid) rather than depending on Shopify's
 * fetch-based variant-update pipeline (VariantUpdateEvent) — that pipeline
 * depends on a fetched fragment's shape that this panel doesn't control
 * end-to-end, so keeping this self-contained is more reliable.
 *
 * This must live on `.quick-add-panel__details` (not the panel's outer root
 * <div>) because morph() runs with `{ childrenOnly: true }` by default (see
 * assets/morph.js and snippets/quick-add-modal.liquid): the root element
 * never survives being swapped into #quick-add-modal-content, only its
 * children do.
 */
export class QuickAddPanelDetails extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#handleSwatchChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleSwatchChange);
  }

  /**
   * @param {Event} event
   */
  #handleSwatchChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const variantId = input.dataset.variantId;
    if (!variantId) return;

    const data = this.#getVariantData(variantId);
    if (!data) return;

    this.#updateMedia(data.image);
    this.#updatePrice(data.price, data.compareAtPrice);
    this.#updateVariantId(variantId, data.available !== false);
  };

  /**
   * @param {string} variantId
   * @returns {{ image: string | null, price: string, compareAtPrice: string | null, available: boolean } | null}
   */
  #getVariantData(variantId) {
    const script = this.parentElement?.querySelector('script[data-quick-add-variants]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;

    try {
      const map = JSON.parse(script.textContent);
      return map[variantId] ?? null;
    } catch {
      return null;
    }
  }

  /**
   * @param {string | null} imageUrl
   */
  #updateMedia(imageUrl) {
    if (!imageUrl) return;

    const media = this.parentElement?.querySelector('.quick-add-panel__media img');
    if (!(media instanceof HTMLImageElement)) return;

    media.removeAttribute('srcset');
    media.removeAttribute('sizes');
    media.src = imageUrl;
  }

  /**
   * @param {string} price
   * @param {string | null} compareAtPrice
   */
  #updatePrice(price, compareAtPrice) {
    const priceContainer = this.querySelector(`.quick-add-panel__price [ref='priceContainer']`);
    if (!priceContainer) return;

    const priceEl = priceContainer.querySelector('.price');
    if (priceEl) priceEl.textContent = price;

    const compareEl = priceContainer.querySelector('.compare-at-price');
    if (compareEl && compareAtPrice) compareEl.textContent = compareAtPrice;
  }

  /**
   * @param {string} variantId
   * @param {boolean} available
   */
  #updateVariantId(variantId, available) {
    const variantIdInput = this.querySelector('input[name="id"]');
    if (variantIdInput instanceof HTMLInputElement) {
      variantIdInput.value = variantId;
      variantIdInput.disabled = !available;
    }

    const submitButton = this.querySelector('button.quick-add-panel__submit');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = !available;
    }
  }
}

if (!customElements.get('quick-add-panel-details')) {
  customElements.define('quick-add-panel-details', QuickAddPanelDetails);
}
